import React, { useEffect, useState } from 'react';
import Paper from '@mui/material/Paper';
import useReport from './useReport';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { formatAmount } from '../../utils/formatAmount';
import { Card, CardContent, Typography, Grid, Button, Box, TableSortLabel } from '@mui/material';
import ShipDateDropdown from '../../sharedComponents/ShipDateDropdown';

const PendingPaymentDetailsReport = () => { 
  const { reportData, isLoading, getCustomerPendingPaymentsReportByShipDate } = useReport();
  const [rowData, setRowData] = useState([]);
  const [summaryData, setSummaryData] = useState(null);
  const [formData, setFormData] = useState({
    startShipDateId: '',
    endShipDateId: ''
  });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  useEffect(() => {
    if (reportData && reportData.data) {
      setRowData(reportData.data.customers || []);
      setSummaryData(reportData.data.summary || null);
    }
  }, [reportData]);

  const handleSubmit = async () => {
    await getCustomerPendingPaymentsReportByShipDate(formData);
  };

  const handleStartShipDateChange = (e) => {
    setFormData({ ...formData, startShipDateId: e.target.value });
  };

  const handleEndShipDateChange = (e) => {
    setFormData({ ...formData, endShipDateId: e.target.value });
  };

  // Handle row click to open customer profile
  const handleRowClick = (smileName) => {
    if (smileName) {
      const url = `/customer-profile?smileName=${encodeURIComponent(smileName)}`;
      window.open(url, '_blank');
    }
  };

  // Handle sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });

    const sortedData = [...rowData].sort((a, b) => {
      let aValue = a[key];
      let bValue = b[key];

      // Handle null/undefined values
      if (aValue == null) aValue = '';
      if (bValue == null) bValue = '';

      // Handle different data types
      if (typeof aValue === 'string') {
        aValue = aValue.toString().toLowerCase();
        bValue = bValue.toString().toLowerCase();
      }

      if (aValue < bValue) {
        return direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    setRowData(sortedData);
  };

  const getSortDirection = (columnKey) => {
    if (sortConfig.key === columnKey) {
      return sortConfig.direction;
    }
    return 'asc'; // Return 'asc' instead of false
  };

  return (
    <div className="space-y-6">
      {/* Ship Date Selection */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Select Ship Date Range
          </Typography>
          <Box className="flex flex-col md:flex-row gap-4 items-start md:items-center">
            <div className="flex flex-col">
              <Typography variant="subtitle2" className="mb-2">
                Start Ship Date
              </Typography>
              <ShipDateDropdown 
                handleDataChange={handleStartShipDateChange}
                includeDefault={true}
              />
            </div>
            <div className="flex flex-col">
              <Typography variant="subtitle2" className="mb-2">
                End Ship Date
              </Typography>
              <ShipDateDropdown 
                handleDataChange={handleEndShipDateChange}
                includeDefault={true}
              />
            </div>
            <Button 
              variant="contained" 
              onClick={handleSubmit}
              disabled={isLoading}
              style={{ backgroundColor: '#4e46e5', marginTop: '20px' }}
            >
              {isLoading ? 'Loading...' : 'Generate Report'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {summaryData && (
        <Grid container spacing={3} className="mb-6">
          <Grid item xs={12} sm={6} md={3}>
            <Card style={{ backgroundColor: '#4e46e5', color: 'white' }}>
              <CardContent>
                <Typography variant="h6" component="div">
                  Total Customers
                </Typography>
                <Typography variant="h4" component="div">
                  {summaryData.totalCustomers}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card style={{ backgroundColor: '#dc2626', color: 'white' }}>
              <CardContent>
                <Typography variant="h6" component="div">
                  Total Unpaid Amount
                </Typography>
                <Typography variant="h4" component="div">
                  {formatAmount(summaryData.totalUnpaidAmount)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card style={{ backgroundColor: '#059669', color: 'white' }}>
              <CardContent>
                <Typography variant="h6" component="div">
                  Total Paid for Non-Arrived
                </Typography>
                <Typography variant="h4" component="div">
                  {formatAmount(summaryData.totalPaidForNonArrived)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card style={{ backgroundColor: '#d97706', color: 'white' }}>
              <CardContent>
                <Typography variant="h6" component="div">
                  Average Unpaid Amount
                </Typography>
                <Typography variant="h4" component="div">
                  {formatAmount(summaryData.averageUnpaidAmount)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Customer Details Table */}
      {rowData.length > 0 && (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 540 }}>
            <Table aria-label="pending payment details table">
              <TableHead stickyHeader style={{ backgroundColor: '#4e46e5', color: 'white' }}>
                <TableRow>
                  <TableCell style={{ color: 'white', fontWeight: 'bold' }}>
                    <TableSortLabel
                      active={sortConfig.key === 'customerId'}
                      direction={sortConfig.key === 'customerId' ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort('customerId')}
                      sx={{ 
                        color: 'white !important',
                        '& .MuiTableSortLabel-icon': {
                          color: 'white !important',
                        }
                      }}
                    >
                      Customer ID
                    </TableSortLabel>
                  </TableCell>
                  <TableCell style={{ color: 'white', fontWeight: 'bold' }}>
                    <TableSortLabel
                      active={sortConfig.key === 'contact'}
                      direction={sortConfig.key === 'contact' ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort('contact')}
                      sx={{ 
                        color: 'white !important',
                        '& .MuiTableSortLabel-icon': {
                          color: 'white !important',
                        }
                      }}
                    >
                      Customer Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell style={{ color: 'white', fontWeight: 'bold' }}>
                    <TableSortLabel
                      active={sortConfig.key === 'smileName'}
                      direction={sortConfig.key === 'smileName' ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort('smileName')}
                      sx={{ 
                        color: 'white !important',
                        '& .MuiTableSortLabel-icon': {
                          color: 'white !important',
                        }
                      }}
                    >
                      Smile Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell style={{ color: 'white', fontWeight: 'bold' }}>
                    <TableSortLabel
                      active={sortConfig.key === 'phone'}
                      direction={sortConfig.key === 'phone' ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort('phone')}
                      sx={{ 
                        color: 'white !important',
                        '& .MuiTableSortLabel-icon': {
                          color: 'white !important',
                        }
                      }}
                    >
                      Phone
                    </TableSortLabel>
                  </TableCell>
                  <TableCell style={{ color: 'white', fontWeight: 'bold' }}>
                    <TableSortLabel
                      active={sortConfig.key === 'totalUnpaidAmount'}
                      direction={sortConfig.key === 'totalUnpaidAmount' ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort('totalUnpaidAmount')}
                      sx={{ 
                        color: 'white !important',
                        '& .MuiTableSortLabel-icon': {
                          color: 'white !important',
                        }
                      }}
                    >
                      Total Unpaid Amount
                    </TableSortLabel>
                  </TableCell>
                  <TableCell style={{ color: 'white', fontWeight: 'bold' }}>
                    <TableSortLabel
                      active={sortConfig.key === 'paymentPercentage'}
                      direction={sortConfig.key === 'paymentPercentage' ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort('paymentPercentage')}
                      sx={{ 
                        color: 'white !important',
                        '& .MuiTableSortLabel-icon': {
                          color: 'white !important',
                        }
                      }}
                    >
                      Payment Percentage
                    </TableSortLabel>
                  </TableCell>
                  <TableCell style={{ color: 'white', fontWeight: 'bold' }}>
                    <TableSortLabel
                      active={sortConfig.key === 'totalPaidForNonArrived'}
                      direction={sortConfig.key === 'totalPaidForNonArrived' ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort('totalPaidForNonArrived')}
                      sx={{ 
                        color: 'white !important',
                        '& .MuiTableSortLabel-icon': {
                          color: 'white !important',
                        }
                      }}
                    >
                      Paid for Non-Arrived
                    </TableSortLabel>
                  </TableCell>
                  <TableCell style={{ color: 'white', fontWeight: 'bold' }}>
                    <TableSortLabel
                      active={sortConfig.key === 'orderCount'}
                      direction={sortConfig.key === 'orderCount' ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort('orderCount')}
                      sx={{ 
                        color: 'white !important',
                        '& .MuiTableSortLabel-icon': {
                          color: 'white !important',
                        }
                      }}
                    >
                      Order Count
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rowData.map((row, index) => (
                  <TableRow 
                    key={row.customerId || index}
                    hover
                    onClick={() => handleRowClick(row.smileName)}
                    style={{ 
                      backgroundColor: index % 2 === 0 ? '#f8fafc' : 'white',
                      cursor: 'pointer'
                    }}
                  >
                    <TableCell>{row.customerId}</TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>{row.contact}</TableCell>
                    <TableCell>{row.smileName}</TableCell>
                    <TableCell>{row.phone}</TableCell>
                    <TableCell style={{ 
                      color: row.totalUnpaidAmount > 0 ? '#dc2626' : '#059669',
                      fontWeight: 'bold'
                    }}>
                      {formatAmount(row.totalUnpaidAmount)}
                    </TableCell>
                    <TableCell style={{ 
                      color: row.paymentPercentage >= 80 ? '#059669' : 
                             row.paymentPercentage >= 50 ? '#d97706' : '#dc2626',
                      fontWeight: 'bold'
                    }}>
                      {row.paymentPercentage}%
                    </TableCell>
                    <TableCell style={{ color: '#059669', fontWeight: 'bold' }}>
                      {formatAmount(row.totalPaidForNonArrived)}
                    </TableCell>
                    <TableCell>{row.orderCount}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {rowData.length === 0 && !isLoading && summaryData && (
        <div className="text-center py-8 text-gray-500">
          <Typography variant="h6">No pending payment data found for the selected ship date range.</Typography>
        </div>
      )}

      {!summaryData && !isLoading && (
        <div className="text-center py-8 text-gray-500">
          <Typography variant="h6">Please select ship date range and click "Generate Report" to view data.</Typography>
        </div>
      )}
    </div>
  );
};

export default PendingPaymentDetailsReport;
